import { meAtom } from "@/atoms/users"
import { Login } from "@/modules/login"
import { SignUp } from "@/features/auth/components/SignUp"
import { SelectOrganization } from "@/modules/spaces/components/SelectOrganization"
import { SelectSpaces } from "@/modules/spaces/components/SelectSpace"
import { Space } from "@/modules/spaces/models/space"
import { daDK, LocalizationProvider, nbNO } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"
import "dayjs/locale/da"
import "dayjs/locale/en"
import "dayjs/locale/nb"
import "dayjs/locale/sv"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import { useCallback, useEffect, useRef, useState } from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { toast } from "sonner"
import { changeSpace, selectedSpaceAtom } from "../../atoms/spaces"
import RedirectProvider from "../../features/redirection/RedirectProvider"
import { useIsMounted } from "../../hooks/useIsMounted"
import {
  AUTH_KEY,
  handleLoginSuccessRedirect,
  requireAuth,
  setItem,
} from "../../utils/browserStorage"
import { dayjsLocaleMapper } from "../../utils/helpers"
import {
  getAuthRenderState,
  isClientRouteSpaceNavigation,
  shouldFinalizeRequestedSpace,
  shouldBlockTabSpaceRestore,
  shouldRunAuthValidation,
  shouldRedirectToLogin,
  shouldWaitForRouteSpace,
} from "../../utils/authRenderState"
import { Loader } from "../spinners/loader"
import { Label } from "@/core/components/Label"
import { useSpaceToken } from "@/modules/spaces/hooks/useSpaceToken"
import { Token } from "@/core/models/token"
import { useSpaceByToken } from "@/modules/spaces/hooks/useSpaceByToken"
import { useValidateUser } from "@/modules/users/hooks/useValidateUser"
import { getService } from "hooks/useStartup"
import { FetchClient } from "@/core/client/fetch_client"
import { applyAuthToken } from "@/utils/api/authentication/applyAuthToken"
import { establishCookieSession } from "@/utils/api/authentication"
import { resolveSpaceIdForReturnUrl } from "@/utils/api/authentication/resolveSpaceForReturnUrl"
import { getSpaceScopedReturnUrl } from "@/utils/spaceScopedRoute"
import type { IAuthUser } from "@/types/auth"
import { COOKIE_AUTH_ENABLED } from "@/core/generated/api/auth_cookies"
import { ResponseModel } from "@/core/generated/api/models/response_model"
import { ResponseSerializer } from "@/core/generated/api/response_serializer"
import { LAST_SELECTED_SPACE_UID_KEY } from "@/utils/tabSpace"
import { isSharedProposalRoute } from "@/features/shared_proposal/sharedRoutes"

export { LAST_SELECTED_SPACE_UID_KEY } from "@/utils/tabSpace"

const isAuthUser = (v: unknown): v is IAuthUser => {
  if (!v || typeof v !== "object") return false
  const r = v as Record<string, unknown>
  return typeof r.uid === "string" && typeof r.email === "string"
}

const getQueryStringValue = (value: string | string[] | undefined) => {
  if (Array.isArray(value)) return value[0] ?? null
  return value ?? null
}

const OrganisationProvider = ({ children }) => {
  const { t } = useTranslation("common")
  const [user, setUser] = useRecoilState(meAtom)
  const [selectedSpace, setSelectedSpace] = useRecoilState(selectedSpaceAtom)
  const [selectedOrganization, setSelectedOrganization] = useState<Space>()
  const [isChangingSpace, setIsChangingSpace] = useRecoilState(changeSpace)
  const router = useRouter()
  const mutation = useSpaceToken()
  const { data: space, loading } = useSpaceByToken()
  const { returnUrl } = router.query
  const requestedSpaceId =
    typeof router.query.spaceId === "string" ? router.query.spaceId : null
  const routeSpaceReturnUrl =
    router.isReady && !requestedSpaceId
      ? getSpaceScopedReturnUrl(router.asPath)
      : null
  const didAutoRestoreSpaceRef = useRef(false)
  const requestedSpaceSwitchRef = useRef<string | null>(null)
  const routeSpaceResolveRef = useRef<string | null>(null)
  const resolvedRouteSpaceRef = useRef<string | null>(null)
  const clientRouteSpaceNavigationRef = useRef<string | null>(null)
  const isRestoringTabSpaceRef = useRef(false)
  const [isSwitchingToRequestedSpace, setIsSwitchingToRequestedSpace] =
    useState(false)
  const [isResolvingRouteSpace, setIsResolvingRouteSpace] = useState(false)
  const [isRestoringTabSpace, setIsRestoringTabSpace] = useState(false)
  const [failedRequestedSpaceId, setFailedRequestedSpaceId] = useState<
    string | null
  >(null)
  const [failedRouteSpaceReturnUrl, setFailedRouteSpaceReturnUrl] = useState<
    string | null
  >(null)
  let organizationId = selectedOrganization?.uid

  if (!organizationId) {
    organizationId = selectedSpace?.isOrganization
      ? selectedSpace.uid
      : (selectedSpace?.organizationId ?? "")
  }

  const switchToSpaceId = useCallback(
    async (spaceId: string): Promise<Space | null> => {
      const token: Token | null = await mutation.mutateAsync(spaceId)

      if (token === null) {
        toast.error("Failed to change space")
        return null
      }

      await applyAuthToken({
        token: token.token,
        cookieAuthEnabled: COOKIE_AUTH_ENABLED,
        establishCookieSession,
        setAuthToken: (value) => setItem(AUTH_KEY, value),
      })
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(LAST_SELECTED_SPACE_UID_KEY, spaceId)
      }

      const client: FetchClient = getService(FetchClient)
      let nextSpace: Space | null = null
      for (let attempt = 0; attempt < 5; attempt++) {
        const spaceResponse = (await client.getAsync(
          "/v1/spaces/me",
        )) as ResponseModel | null
        nextSpace = spaceResponse
          ? ResponseSerializer.deserialize<Space>(spaceResponse)
          : null

        if (nextSpace?.uid === spaceId) break
        if (attempt < 4) {
          await new Promise((resolve) => setTimeout(resolve, 100))
        }
      }

      if (nextSpace?.uid !== spaceId) {
        toast.error("Failed to change space")
        return null
      }

      if (selectedSpace?.uid !== nextSpace.uid) {
        setSelectedSpace(nextSpace)
      }

      const me = (await client.getAsync("/me")) as unknown
      if (
        isAuthUser(me) &&
        (me.space !== user?.space || me.space_user_uid !== user?.space_user_uid)
      ) {
        setUser(me)
      }

      return nextSpace
    },
    [
      mutation,
      selectedSpace?.uid,
      setSelectedSpace,
      setUser,
      user?.space,
      user?.space_user_uid,
    ],
  )

  const setSpace = async (space: Space) => {
    if (space) {
      const nextSpace = await switchToSpaceId(space.uid)
      if (!nextSpace) return

      const pathname = (returnUrl ?? "/") as string
      router.push(pathname)
      setIsChangingSpace(false)
    }
  }

  useEffect(() => {
    if (!router.isReady) return

    const handleRouteChangeStart = (url: string) => {
      clientRouteSpaceNavigationRef.current = getSpaceScopedReturnUrl(url)
    }

    router.events.on("routeChangeStart", handleRouteChangeStart)

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart)
    }
  }, [router.events, router.isReady])

  const restoreSavedSpaceForTab = useCallback(async () => {
    if (typeof window === "undefined") return
    if (!user?.uid) return
    if (loading) return
    if (isChangingSpace) return
    if (requestedSpaceId) return
    if (routeSpaceReturnUrl) return
    if (isRestoringTabSpaceRef.current) return

    const savedUid = window.sessionStorage.getItem(LAST_SELECTED_SPACE_UID_KEY)
    if (!savedUid) return

    const shouldBlockRender = shouldBlockTabSpaceRestore({
      savedSpaceUid: savedUid,
      selectedSpaceUid: selectedSpace?.uid,
    })

    isRestoringTabSpaceRef.current = true
    if (shouldBlockRender) {
      setIsRestoringTabSpace(true)
    }

    try {
      const client: FetchClient = getService(FetchClient)
      const spaceResponse = (await client.getAsync(
        "/v1/spaces/me",
      )) as ResponseModel | null
      const tokenSpace = spaceResponse
        ? ResponseSerializer.deserialize<Space>(spaceResponse)
        : null

      if (tokenSpace?.uid === savedUid) {
        if (selectedSpace?.uid !== savedUid) {
          setSelectedSpace(tokenSpace)
        }
        return
      }

      await switchToSpaceId(savedUid)
    } finally {
      isRestoringTabSpaceRef.current = false
      if (shouldBlockRender) {
        setIsRestoringTabSpace(false)
      }
    }
  }, [
    user?.uid,
    loading,
    isChangingSpace,
    requestedSpaceId,
    routeSpaceReturnUrl,
    selectedSpace?.uid,
    setSelectedSpace,
    switchToSpaceId,
  ])

  useEffect(() => {
    if (!requestedSpaceId) return
    if (loading) return
    if (!user?.uid) return
    if (
      shouldFinalizeRequestedSpace({
        requestedSpaceId,
        selectedSpaceUid: selectedSpace?.uid,
      })
    ) {
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(
          LAST_SELECTED_SPACE_UID_KEY,
          requestedSpaceId,
        )
      }

      const query = { ...router.query }
      delete query.spaceId
      router.replace({ pathname: router.pathname, query }, undefined, {
        shallow: true,
      })
      return
    }
    if (failedRequestedSpaceId === requestedSpaceId) return
    if (requestedSpaceSwitchRef.current === requestedSpaceId) return

    requestedSpaceSwitchRef.current = requestedSpaceId
    setIsSwitchingToRequestedSpace(true)

    switchToSpaceId(requestedSpaceId)
      .then((space) => {
        if (requestedSpaceSwitchRef.current !== requestedSpaceId) return
        if (!space) setFailedRequestedSpaceId(requestedSpaceId)
        if (space) {
          const query = { ...router.query }
          delete query.spaceId
          router.replace({ pathname: router.pathname, query }, undefined, {
            shallow: true,
          })
        }
      })
      .finally(() => {
        if (requestedSpaceSwitchRef.current !== requestedSpaceId) return
        requestedSpaceSwitchRef.current = null
        setIsSwitchingToRequestedSpace(false)
      })
  }, [
    requestedSpaceId,
    loading,
    user?.uid,
    selectedSpace?.uid,
    failedRequestedSpaceId,
    router,
    switchToSpaceId,
  ])

  useEffect(() => {
    if (!routeSpaceReturnUrl) return
    if (loading) return
    if (!user?.uid) return
    if (isChangingSpace) return
    if (requestedSpaceId) return
    if (
      isClientRouteSpaceNavigation({
        routeSpaceReturnUrl,
        clientRouteSpaceReturnUrl: clientRouteSpaceNavigationRef.current,
      })
    ) {
      resolvedRouteSpaceRef.current = routeSpaceReturnUrl
      clientRouteSpaceNavigationRef.current = null
      return
    }
    if (failedRouteSpaceReturnUrl === routeSpaceReturnUrl) return
    if (resolvedRouteSpaceRef.current === routeSpaceReturnUrl) return
    if (routeSpaceResolveRef.current === routeSpaceReturnUrl) return

    routeSpaceResolveRef.current = routeSpaceReturnUrl
    setIsResolvingRouteSpace(true)

    resolveSpaceIdForReturnUrl(routeSpaceReturnUrl)
      .then(async (spaceId) => {
        if (routeSpaceResolveRef.current !== routeSpaceReturnUrl) return

        if (!spaceId) {
          setFailedRouteSpaceReturnUrl(routeSpaceReturnUrl)
          return
        }

        if (selectedSpace?.uid === spaceId) {
          if (typeof window !== "undefined") {
            window.sessionStorage.setItem(LAST_SELECTED_SPACE_UID_KEY, spaceId)
          }
          resolvedRouteSpaceRef.current = routeSpaceReturnUrl
          return
        }

        const space = await switchToSpaceId(spaceId)
        if (space) {
          resolvedRouteSpaceRef.current = routeSpaceReturnUrl
          return
        }

        setFailedRouteSpaceReturnUrl(routeSpaceReturnUrl)
      })
      .catch(() => {
        if (routeSpaceResolveRef.current === routeSpaceReturnUrl) {
          setFailedRouteSpaceReturnUrl(routeSpaceReturnUrl)
        }
      })
      .finally(() => {
        if (routeSpaceResolveRef.current !== routeSpaceReturnUrl) return
        routeSpaceResolveRef.current = null
        setIsResolvingRouteSpace(false)
      })
  }, [
    routeSpaceReturnUrl,
    loading,
    user?.uid,
    isChangingSpace,
    requestedSpaceId,
    failedRouteSpaceReturnUrl,
    selectedSpace?.uid,
    switchToSpaceId,
  ])

  useEffect(() => {
    if (space !== null) {
      setSelectedSpace(space)
      setIsChangingSpace(false)
    }
  }, [space, setIsChangingSpace, setSelectedSpace])

  useEffect(() => {
    if (typeof window === "undefined") return
    if (!user?.uid) return
    if (!selectedSpace?.uid) return

    const savedUid = window.sessionStorage.getItem(LAST_SELECTED_SPACE_UID_KEY)
    if (savedUid && savedUid !== selectedSpace.uid) return

    window.sessionStorage.setItem(
      LAST_SELECTED_SPACE_UID_KEY,
      selectedSpace.uid,
    )
  }, [user?.uid, selectedSpace?.uid])

  useEffect(() => {
    if (didAutoRestoreSpaceRef.current) return
    if (typeof window === "undefined") return
    if (loading) return
    if (isChangingSpace) return
    if (requestedSpaceId) return
    if (routeSpaceReturnUrl) return
    if (!user?.uid) return

    const savedUid = window.sessionStorage.getItem(LAST_SELECTED_SPACE_UID_KEY)
    if (!savedUid) return
    if (selectedSpace?.uid === savedUid) {
      didAutoRestoreSpaceRef.current = true
      return
    }

    didAutoRestoreSpaceRef.current = true
    void restoreSavedSpaceForTab()
  }, [
    loading,
    isChangingSpace,
    user?.uid,
    selectedSpace?.uid,
    requestedSpaceId,
    routeSpaceReturnUrl,
    restoreSavedSpaceForTab,
  ])

  useEffect(() => {
    if (typeof window === "undefined") return

    const restoreSpace = () => {
      void restoreSavedSpaceForTab()
    }
    const restoreIfVisible = () => {
      if (document.visibilityState === "hidden") return
      void restoreSavedSpaceForTab()
    }

    window.addEventListener("focus", restoreSpace)
    window.addEventListener("pageshow", restoreSpace)
    document.addEventListener("visibilitychange", restoreIfVisible)

    return () => {
      window.removeEventListener("focus", restoreSpace)
      window.removeEventListener("pageshow", restoreSpace)
      document.removeEventListener("visibilitychange", restoreIfVisible)
    }
  }, [restoreSavedSpaceForTab])
  useEffect(() => {
    if (user?.uid) {
      if (window._cio) {
        window._cio.identify({
          id: user.space_user_uid,
          email: user.email,
          firstname: user.firstname,
          lastname: user.lastname,
          space: user.space,
          created_at: Math.round(new Date(user.created).getTime() / 1000),
          updated_at: Math.round(new Date(user.updated).getTime() / 1000),
        })

        window._cio.page(router.asPath)
      }
    }
  }, [router.asPath, user])

  const waitingForRequestedSpace =
    !!requestedSpaceId &&
    selectedSpace?.uid !== requestedSpaceId &&
    failedRequestedSpaceId !== requestedSpaceId
  const waitingForRouteSpace = shouldWaitForRouteSpace({
    routeSpaceReturnUrl,
    resolvedRouteSpaceReturnUrl: resolvedRouteSpaceRef.current,
    failedRouteSpaceReturnUrl,
    clientRouteSpaceReturnUrl: clientRouteSpaceNavigationRef.current,
  })

  if (
    loading ||
    isSwitchingToRequestedSpace ||
    isResolvingRouteSpace ||
    isRestoringTabSpace ||
    waitingForRequestedSpace ||
    waitingForRouteSpace
  ) {
    return <Loader />
  }

  if (isChangingSpace || !selectedSpace) {
    return (
      <div className="h-screen w-screen overflow-hidden bg-blue-100">
        <div className="mx-auto flex max-h-screen max-w-4xl flex-col space-y-4 py-4">
          <div>
            <Label className="mb-2 text-sm font-bold">
              {t("organization")}
            </Label>
            <SelectOrganization
              autoSelect
              selectedOrganizationId={organizationId}
              setSelectedOrganization={setSelectedOrganization}
            />
          </div>
          <div className="flex flex-1 flex-col overflow-hidden">
            <Label className="mb-2 text-sm font-bold">{t("spaces")}</Label>
            <SelectSpaces
              selectedOrganization={selectedOrganization}
              onSelect={setSpace}
            />
          </div>
          <div className="my-4 w-full border"></div>
        </div>
      </div>
    )
  }

  return children
}

const useAuthValidator = ({
  disabled = false,
}: {
  disabled?: boolean
} = {}) => {
  const isMounted = useIsMounted()
  const setUser = useSetRecoilState(meAtom)
  const router = useRouter()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false)
  const callbackToken = router.isReady
    ? getQueryStringValue(router.query["_ft_c"])
    : null

  const fetchMe = useCallback(async () => {
    try {
      const client: FetchClient = getService(FetchClient)
      const data = (await client.getAsync("/me")) as unknown

      if (isAuthUser(data)) {
        setIsLoggedIn(true)
        setUser(data)
        return true
      }

      return false
    } catch (error) {
      return false
    } finally {
      setIsLoading(false)
    }
  }, [setUser])

  useEffect(() => {
    if (disabled) return
    if (!router.isReady) return
    if (isMounted()) {
      if (callbackToken) {
        const completeLogin = async () => {
          const token = callbackToken
          try {
            window.sessionStorage.removeItem(LAST_SELECTED_SPACE_UID_KEY)
            setItem(AUTH_KEY, token)

            await establishCookieSession(token)
            const isValidated = await fetchMe()
            if (!isValidated) {
              throw new Error("Failed to validate login")
            }

            const { _ft_c, ...rest } = router.query

            await router.replace(
              {
                pathname: router.pathname,
                query: rest,
              },
              undefined,
              { shallow: true },
            )

            handleLoginSuccessRedirect(token)
          } catch (error) {
            setIsLoading(false)
            toast.error(error.message)
          }
        }
        completeLogin()
      }
    }
  }, [callbackToken, disabled, fetchMe, isMounted, router])

  useEffect(() => {
    if (disabled) {
      setIsLoading(false)
      return
    }
    if (
      !shouldRunAuthValidation({
        disabled,
        isRouterReady: router.isReady,
        callbackToken,
      })
    ) {
      return
    }

    if (isMounted()) {
      fetchMe()
    }

    const setLastOnline = () => {
      const dateTime = new Date().toISOString()
      localStorage.setItem("lastOnline", dateTime)
    }

    window.addEventListener("beforeunload", setLastOnline)

    return () => {
      window.removeEventListener("beforeunload", setLastOnline)
    }
  }, [callbackToken, isMounted, fetchMe, disabled, router.isReady])

  return { isLoading, isLoggedIn }
}

function AuthenticationProvider({ children }) {
  const [mounted, setMounted] = useState<boolean>(false)

  const isMounted = useIsMounted()
  const { i18n } = useTranslation()
  const user = useRecoilValue(meAtom)
  const router = useRouter()

  const isRedirectRoute =
    router.pathname === "/r" ||
    router.pathname === "/r/[code]" ||
    // Public shared-proposal routes use their own scoped-session auth and must
    // NOT trigger the employee auth gate / login redirect. Treated as a
    // passthrough, identical to the redirect route.
    isSharedProposalRoute(router.pathname)
  const { isLoading, isLoggedIn } = useAuthValidator({
    disabled: isRedirectRoute,
  })
  const { signedUp } = useValidateUser({ userId: user?.uid ?? "" })
  const isAuthenticatedUser = isLoggedIn && !!user?.uid
  const isLoginRoute = router.pathname === "/login"
  const authRenderState = getAuthRenderState({
    isRedirectRoute,
    isAuthenticatedUser,
    isLoginRoute,
  })

  useEffect(() => {
    if (isMounted()) {
      setMounted(true)
    }
  }, [isMounted])

  useEffect(() => {
    if (!mounted) return
    if (
      !shouldRedirectToLogin({
        isLoading,
        isRedirectRoute,
        isAuthenticatedUser,
        isLoginRoute,
      })
    ) {
      return
    }

    requireAuth({ ignoreExistingAuth: true })
  }, [
    mounted,
    isLoading,
    isRedirectRoute,
    isAuthenticatedUser,
    isLoginRoute,
    router.asPath,
  ])

  if (!mounted) return null
  if (isLoading) return <Loader />

  const localeObject = {
    da: daDK,
    nb: nbNO,
  }
  const rawLocaleText =
    localeObject[i18n.language]?.components?.MuiLocalizationProvider
      ?.defaultProps?.localeText
  const localeText =
    rawLocaleText &&
    typeof rawLocaleText === "object" &&
    !Array.isArray(rawLocaleText)
      ? rawLocaleText
      : {}

  return (
    <RedirectProvider>
      <LocalizationProvider
        adapterLocale={i18n.language}
        dateAdapter={AdapterDayjs}
        localeText={localeText}
      >
        {authRenderState === "redirect-route" ? (
          children
        ) : authRenderState === "authenticated" ? (
          <OrganisationProvider>
            {signedUp === false ? <SignUp /> : children}
          </OrganisationProvider>
        ) : authRenderState === "login" ? (
          <Login />
        ) : (
          <Loader />
        )}
      </LocalizationProvider>
    </RedirectProvider>
  )
}

export default AuthenticationProvider
